import { createRoot } from 'react-dom/client';
import { HashRouter, BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { registerServiceWorker } from '@/integrations/supabase/client';
// Import useAppStore to ensure its initialization logic runs
import { useAppStore } from '@/store';
import { initializeRemoteLogger } from '@/lib/remote-logger';
import { HelmetProvider } from 'react-helmet-async';

// Initialize remote logging for production environments
initializeRemoteLogger();

// 👇 Detect if running inside Electron
const isElectron = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('electron');
const Router = isElectron ? HashRouter : BrowserRouter;
const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

console.log(`[Router] Using ${isElectron ? 'HashRouter' : 'BrowserRouter'}`);

// Register service worker for PWA functionality (only in web prod)
if (!isElectron && process.env.NODE_ENV === 'production') {
  registerServiceWorker()
    .then(registration => {
      console.log('[SW] Registered successfully:', registration);
    })
    .catch(err => {
      console.error('[SW] Registration failed:', err);
    });
} else if (!isElectron) {
  // Dev mode (web only): unregister all service workers
  if ('serviceWorker' in navigator && typeof navigator.serviceWorker?.getRegistrations === 'function') {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (const registration of registrations) {
        console.log('[SW] Unregistering in dev mode:', registration);
        registration.unregister();
      }
    }).catch(err => {
      console.warn('[SW] Failed to enumerate registrations in dev mode:', err);
    });
  } else {
    console.info('[SW] Service workers not supported/available in this environment (dev).');
  }
}

// Ensure landing page uses window scroll before first paint (prevents flicker)
if (window.location.pathname === '/') {
  document.documentElement.classList.add('allow-window-scroll');
}

// Define typography classes in the root to ensure they're available
const setTypographyClasses = () => {
  const style = document.createElement('style');
  style.textContent = `
    .prose h1, .prose h2, .prose h3, .prose h4 {
      font-weight: 600;
      margin-top: 1.5em;
      margin-bottom: 0.5em;
    }
    .prose strong {
      font-weight: 600;
    }
    .prose p {
      margin-bottom: 1em;
    }
    .prose ol, .prose ul {
      margin-top: 0.5em;
      margin-bottom: 1em;
      padding-left: 1.5em;
    }
    .prose li {
      margin-bottom: 0.5em;
    }
    .prose a {
      color: hsl(var(--primary));
      text-decoration: underline;
    }
    .prose blockquote {
      border-left: 3px solid hsl(var(--border));
      padding-left: 1em;
      font-style: italic;
    }
  `;
  document.head.appendChild(style);
};

// Call the typography function before the app renders
setTypographyClasses();

// Create the root and render the app
const root = createRoot(document.getElementById("root")!);

root.render(
  <Router future={routerFuture}>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </Router>
);